import {
  ACTION,
  HedgeCommandEnum,
  HedgeControlStatusEnum,
  IconName,
  IndicatorBadgeVariants,
  MarketAccountStatusEnum,
  MixpanelEvent,
  MixpanelEventSource,
  ModeEnum,
  useDisclosure,
  useMarketAccountsContext,
  useMixpanel,
  useSyncedRef,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useFeatureFlag, useRoleAuth } from '../../../../hooks';
import { OrgConfigurationKey, useOrgConfiguration, usePricingRules } from '../../../../providers';
import { useHedgePositionStatus } from '../../../../providers/HedgePositionStatusProvider';

import { useHedgerStatus } from '../../../../hooks/useHedgerStatus';
import { useHedgeRuleCommandMutation } from '../../../Trading/Markets/PositionAutoHedgingRules/queries';
import { useAllTradableMarketAccountNames } from '../useAllTradableMarketAccountNames';
import type { TradingControlsDialogInfo, TradingControlsDialogProps, TradingControlsForm } from './types';

export function useTradingControlsDialog(): TradingControlsDialogProps {
  const { isAuthorized } = useRoleAuth();
  const { enableTradingControls, enableAutoHedging, enableDynamicDeltaHedging } = useFeatureFlag();
  const { getConfig, disableTrading, enableTrading } = useOrgConfiguration();
  const dialog = useDisclosure();
  const { globalDefault, hasGlobalDefault, isGlobalDefaultLoading, updatePricingRule } = usePricingRules();
  const { globalHedgeRule } = useHedgePositionStatus();
  const hedgerStatus = useHedgerStatus();
  const sendHedgeCommandMutation = useHedgeRuleCommandMutation();
  const allTradableMarketAccountNames = useAllTradableMarketAccountNames();
  const { marketAccountsByName, marketAccountDisplayNameByName } = useMarketAccountsContext();
  const marketAccountsByNameRef = useSyncedRef(marketAccountsByName);
  const mixpanel = useMixpanel(MixpanelEventSource.TradingControls);

  // Show certain sections based on feature flags and/or rights
  const showPositionAutoHedgingInfo = enableAutoHedging;
  const showCustomerTradingInfo = isAuthorized(ACTION.EDIT_DEALER_TRADING);
  const showDdhInfo = enableDynamicDeltaHedging;

  // Show the dialog only when feature is enabled and current user is allowed to view it
  const enabled = enableTradingControls && isAuthorized(ACTION.VIEW_TRADING_CONTROLS_DIALOG);

  // Global Trading is enabled
  const tradingEnabled = getConfig(OrgConfigurationKey.TradingDisableAll, '0') !== '1';

  // Customer Trading is enabled
  const customerTradingEnabled = globalDefault?.Mode === ModeEnum.Enabled;

  // Position Auto-Hedging is enabled
  const positionAutoHedgingEnabled =
    globalHedgeRule !== undefined && globalHedgeRule.HedgeControlStatus !== HedgeControlStatusEnum.Disabled;

  // Dynamic Delta Hedging is enabled
  const ddhEnabled = hedgerStatus !== undefined && hedgerStatus?.OrderHedgingStatus !== HedgeControlStatusEnum.Disabled;

  // When still loading, show loading spinner and make Trading Controls button disabled
  const loading =
    (showCustomerTradingInfo && isGlobalDefaultLoading) || (positionAutoHedgingEnabled && !globalHedgeRule);

  const allIsEnabled = tradingEnabled && customerTradingEnabled && positionAutoHedgingEnabled && ddhEnabled;

  // Information for disabled Market Accounts
  const disabledMarketAccounts = useMemo(() => {
    return compact(
      allTradableMarketAccountNames
        .filter(
          marketAccount =>
            marketAccountsByNameRef.current.get(marketAccount)?.Status === MarketAccountStatusEnum.Disabled
        )
        .toSorted()
    );
  }, [allTradableMarketAccountNames, marketAccountsByNameRef]);

  const updateControls = useCallback(
    (initialForm: TradingControlsForm, form: TradingControlsForm) => {
      const promises: Promise<any>[] = [];
      // Global Trading
      if (initialForm.tradingEnabled !== form.tradingEnabled) {
        promises.push(form.tradingEnabled ? enableTrading() : disableTrading());
        mixpanel.track(
          form.tradingEnabled ? MixpanelEvent.TradingControlsEnableTrading : MixpanelEvent.TradingControlsDisableTrading
        );
      }

      // Position Auto-Hedging
      if (initialForm.positionAutoHedgingEnabled !== form.positionAutoHedgingEnabled) {
        promises.push(
          sendHedgeCommandMutation.mutateAsync({
            Command: form.positionAutoHedgingEnabled ? HedgeCommandEnum.Activate : HedgeCommandEnum.Kill,
          })
        );
        mixpanel.track(
          form.positionAutoHedgingEnabled
            ? MixpanelEvent.TradingControlsEnablePositionAutoHedging
            : MixpanelEvent.TradingControlsDisablePositionAutoHedging
        );
      }

      // Dynamic Delta Hedging, DDH
      if (initialForm.ddhEnabled !== form.ddhEnabled) {
        promises.push(
          sendHedgeCommandMutation.mutateAsync({
            Command: form.ddhEnabled ? HedgeCommandEnum.Activate : HedgeCommandEnum.Kill,
            HedgeControlType: 'Order',
          })
        );
        mixpanel.track(
          form.ddhEnabled
            ? MixpanelEvent.TradingControlsEnableDDHTrading
            : MixpanelEvent.TradingControlsDisableDDHTrading
        );
      }

      // Customer Trading
      if (hasGlobalDefault && initialForm.customerTradingEnabled !== form.customerTradingEnabled) {
        promises.push(
          updatePricingRule({
            ...globalDefault,
            Mode: form.customerTradingEnabled ? ModeEnum.Enabled : ModeEnum.Disabled,
          })
        );
        mixpanel.track(
          form.customerTradingEnabled
            ? MixpanelEvent.TradingControlsEnableCustomerTrading
            : MixpanelEvent.TradingControlsDisableCustomerTrading
        );
      }

      return Promise.allSettled(promises);
    },
    [
      updatePricingRule,
      globalDefault,
      hasGlobalDefault,
      enableTrading,
      disableTrading,
      sendHedgeCommandMutation,
      mixpanel,
    ]
  );

  return {
    dialog,
    enabled,
    loading,
    allIsEnabled,
    tradingEnabled,
    customerTradingEnabled,
    positionAutoHedgingEnabled,
    ddhEnabled,
    dialogInfo: getDialogInfo(
      loading,
      tradingEnabled,
      customerTradingEnabled,
      positionAutoHedgingEnabled,
      ddhEnabled,
      disabledMarketAccounts,
      showPositionAutoHedgingInfo,
      showCustomerTradingInfo,
      showDdhInfo,
      marketAccountDisplayNameByName
    ),
    disabledMarketAccounts,
    updateControls,
    showPositionAutoHedgingInfo,
    showCustomerTradingInfo,
    showDdhInfo,
    hasGlobalDefault,
  };
}

function getDialogInfo(
  loading: boolean,
  tradingEnabled: boolean,
  customerTradingEnabled: boolean,
  positionAutoHedgingEnabled: boolean,
  ddhEnabled: boolean,
  disabledMarketAccounts: string[],
  showPositionAutoHedgingInfo: boolean,
  showCustomerTradingInfo: boolean,
  showDdhInfo: boolean,
  marketAccountDisplayNameByName: Map<string, string>
): TradingControlsDialogInfo | undefined {
  if (loading) {
    return {
      iconName: IconName.Check,
      iconColor: 'colors.green.lighten',
      loading,
      variant: IndicatorBadgeVariants.Positive,
    };
  }
  if (!tradingEnabled) {
    return {
      iconName: IconName.ExclamationSolid,
      iconColor: 'colors.red.lighten',
      variant: IndicatorBadgeVariants.Negative,
      warnings: ['Trading is disabled.'],
    };
  }
  const warnings: string[] = [];
  if (showPositionAutoHedgingInfo && !positionAutoHedgingEnabled) {
    warnings.push('Position Auto-Hedging is disabled.');
  }
  if (showCustomerTradingInfo && !customerTradingEnabled) {
    warnings.push('Customer Trading is disabled.');
  }
  if (showDdhInfo && !ddhEnabled) {
    warnings.push('Dynamic Delta Hedging is disabled.');
  }
  if (disabledMarketAccounts.length > 0) {
    warnings.push(
      `Disabled Market Accounts (${disabledMarketAccounts.length}): ${disabledMarketAccounts
        .map(marketAccount => marketAccountDisplayNameByName?.get(marketAccount))
        .join(', ')} `
    );
  }
  if (warnings.length > 0) {
    return {
      iconName: IconName.ExclamationSolid,
      iconColor: 'colors.yellow.lighten',
      variant: IndicatorBadgeVariants.Warning,
      warnings,
      counter: warnings.length,
    };
  }
  return {
    iconName: IconName.Check,
    iconColor: 'colors.green.lighten',
    variant: IndicatorBadgeVariants.Positive,
  };
}
